import * as React from 'react';
import { TextField } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import { InputProps as StandardInputProps } from '@mui/material/Input/Input';

interface IDNumberFieldProps {
    // Coerced internally: null/undefined render as empty, numbers are
    // stringified (initial values often arrive as numbers).
    value: string | number | null | undefined;
    // Always emits the clean numeric string ("1234.56"), never the formatted
    // display — commas are display-only, so `yup.number()` (or `Number()`)
    // coerces it cleanly. `source` distinguishes real typing ('event') from
    // programmatic value updates ('prop') so wrappers can keep their own
    // semantics — RhNumberField stores every change but notifies its external
    // onChange only on typing.
    onChange: (value: string, source: 'event' | 'prop') => void;
    label?: string;
    error?: boolean;
    // Widened from the shared string-only prop (like RHTextField) so callers
    // can pass a node — e.g. the transfer amount's "Aplicar" hint link.
    helperText?: React.ReactNode;
    disabled?: boolean;
    disableGutters?: boolean;
    InputProps?: Partial<StandardInputProps>;
    name?: string;
    onBlur?: () => void;
    inputRef?: React.Ref<HTMLInputElement>;
}

// Standalone number input with live thousands-comma grouping (1,234,567.89,
// dot-decimal — the Mexico convention, matching `formatFloat`). Typing is
// restricted to digits, ",", "." and a leading "-" by `NumericFormat` itself.
// Pure controlled twin of `RhNumberField` (which wraps this with
// react-hook-form wiring), following the DTextField/RHTextField convention —
// usable in filters, toolbars, and dialogs that have no FormProvider.
export function DNumberField({
    value,
    onChange,
    label,
    error = false,
    helperText,
    disabled = false,
    disableGutters = false,
    InputProps,
    name,
    onBlur,
    inputRef,
}: IDNumberFieldProps) {
    return (
        <NumericFormat
            // --- formatting / input restriction ---
            thousandSeparator=","
            decimalSeparator="."
            decimalScale={2}
            allowNegative
            valueIsNumericString
            value={
                value === null || value === undefined ? '' : String(value)
            }
            onValueChange={(values, sourceInfo) => {
                onChange(values.value, sourceInfo.source);
            }}
            getInputRef={inputRef}
            onBlur={onBlur}
            // --- MUI TextField props (forwarded through customInput) ---
            customInput={TextField}
            name={name}
            label={label}
            InputProps={InputProps}
            error={error}
            helperText={helperText || ''}
            margin={'dense'}
            size={'small'}
            disabled={disabled}
            sx={{ my: !disableGutters ? 2 : 0, width: '100%' }}
        />
    );
}
